import { useState, useRef, useEffect } from 'react'
import { useMeeting } from '../../contexts/MeetingContext'
import { useRealtimeReuniao } from '../../hooks/useRealtimeReuniao'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import useDraggable from '../../hooks/useDraggable'
import { fmtTime } from './AoVivoSidebar'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import NotasEditorLazy from './NotasEditorLazy'

const BUCKET = 'reunioes'

interface TempImage {
  tempId: number
  dbId?: string
  localUrl?: string
  remotePath?: string
  uploading: boolean
}

export default function MeetingCompact() {
  const {
    meeting, seconds, paused,
    updateMeeting, endMeeting, cancelMeeting,
    toFullscreen, toCapsule, pauseMeeting, resumeMeeting,
  } = useMeeting()

  const reuniao   = meeting?.reuniao
  const rt = useRealtimeReuniao(reuniao?.id)

  // ── Drag ──
  const defaultX = Math.round((window.innerWidth - 380) / 2)
  const defaultY = Math.round((window.innerHeight - 480) / 2)
  const { pos, onPointerDown, wasDrag } = useDraggable(
    { x: defaultX, y: defaultY },
    { minX: 0, minY: 0, maxX: window.innerWidth - 380, maxY: window.innerHeight - 60 }
  )

  // ── Notas ──
  const [notas, setNotas] = useState<string>(reuniao?.notas_reuniao || '')
  const notasTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notasOwnerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reuniaoId = reuniao?.id
  useEffect(() => {
    if (!reuniaoId) return
    setNotas(reuniao?.notas_reuniao || '')
    setImagens([])
  }, [reuniaoId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { clearTimeout(notasTimer.current); clearTimeout(notasOwnerTimer.current) }, [])

  // Sync notas de outro device
  useEffect(() => {
    if (rt.remoteNotas !== null && !rt.isNotasOwner) setNotas(rt.remoteNotas)
  }, [rt.remoteNotas]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleNotas(val: string) {
    setNotas(val)
    rt.claimNotasOwnership()
    clearTimeout(notasOwnerTimer.current)
    notasOwnerTimer.current = setTimeout(() => rt.releaseNotasOwnership(), 5000)
    clearTimeout(notasTimer.current)
    notasTimer.current = setTimeout(() => updateMeeting({ notas_reuniao: val }).catch(() => {}), 3000)
  }

  // ── Imagens ──
  const [imagens, setImagens] = useState<TempImage[]>([])

  async function uploadImage(blob: Blob) {
    const ext = blob.type.split('/')[1] || 'png'
    const path = `${reuniao.id}/img_${Date.now()}.${ext}`
    const tempId = Date.now() + Math.random()
    setImagens(prev => [...prev, { tempId, localUrl: URL.createObjectURL(blob), uploading: true }])
    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: true })
      if (error) throw error
      await supabase.from('reuniao_imagens').insert({ reuniao_id: reuniao.id, storage_path: path })
      setImagens(prev => prev.map(i => i.tempId === tempId ? { ...i, uploading: false } : i))
    } catch { toast.error('Erro ao salvar imagem'); setImagens(prev => prev.filter(i => i.tempId !== tempId)) }
  }

  // Remote images
  useEffect(() => {
    if (!rt.remoteImagens.length) return
    const last = rt.remoteImagens[rt.remoteImagens.length - 1]
    setImagens(prev => {
      if (prev.some(i => i.dbId === last.id)) return prev
      return [...prev, { tempId: last.id, dbId: last.id, uploading: false, remotePath: last.storage_path }]
    })
  }, [rt.remoteImagens.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Remote end
  useEffect(() => {
    if (rt.remoteStatus === 'REALIZADA') { toast.info('Reunião encerrada em outro dispositivo'); endMeeting() }
  }, [rt.remoteStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  const [showEncerrarDlg, setShowEncerrarDlg] = useState<boolean>(false)

  if (!reuniao) return null

  return (
    <>
    <AlertDialog open={showEncerrarDlg} onOpenChange={setShowEncerrarDlg}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Encerrar reunião?</AlertDialogTitle>
          <AlertDialogDescription>
            Para encerrar com gravação de áudio e transcrição, use a tela cheia.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setShowEncerrarDlg(false); toFullscreen() }}
            className="bg-white/10 hover:bg-white/15 text-[#f0f0f0]">
            Ir para tela cheia
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <div
      className="fixed z-[275] flex flex-col bg-surface rounded-2xl overflow-hidden select-none"
      style={{
        width: 380, height: 480,
        left: pos?.x ?? defaultX, top: pos?.y ?? defaultY,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)',
      }}
    >
      {/* Header — draggable */}
      <div
        onPointerDown={onPointerDown}
        className="flex items-center gap-2 px-4 py-3 border-b border-white/5 cursor-grab active:cursor-grabbing shrink-0"
      >
        {/* Pulsing indicator */}
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inset-0 rounded-full bg-hero opacity-75" />
          <span className="relative w-2 h-2 rounded-full bg-hero" />
        </span>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.09em] uppercase text-muted">Reunião ao vivo</p>
          <h3 className="text-[13px] font-semibold text-[#f0f0f0] truncate leading-tight">{reuniao.titulo}</h3>
        </div>

        <span className="text-lg font-light text-[#f0f0f0] tabular-nums shrink-0">{fmtTime(seconds)}</span>

        {/* Controls */}
        <button onClick={() => { if (!wasDrag.current) toFullscreen() }} title="Tela cheia"
          className="h-6 w-6 rounded-full flex items-center justify-center bg-transparent border border-white/[0.07] cursor-pointer text-[#555] hover:text-[#f0f0f0] transition-colors shrink-0">
          <span className="material-symbols-outlined text-[13px]">open_in_full</span>
        </button>
        <button onClick={() => { if (!wasDrag.current) toCapsule() }} title="Minimizar para cápsula"
          className="h-6 w-6 rounded-full flex items-center justify-center bg-transparent border border-white/[0.07] cursor-pointer text-[#555] hover:text-[#f0f0f0] transition-colors shrink-0">
          <span className="material-symbols-outlined text-[13px]">close_fullscreen</span>
        </button>
      </div>

      {/* Notas */}
      <div className="flex-1 flex flex-col p-3 min-h-0">
        <p className="text-[11px] tracking-[0.09em] uppercase text-[#999] font-bold mb-1">Anotações</p>
        <NotasEditorLazy
          value={notas}
          onChange={handleNotas}
          editable={rt.isNotasOwner}
          onImageFile={uploadImage}
          ariaLabel="Anotações da reunião"
          placeholder="Digite suas anotações…"
          className={cn(
            'notas-editor flex-1 min-h-0 rounded-xl overflow-hidden bg-white/[0.02] border transition-colors',
            rt.isNotasOwner ? 'border-white/[0.05] focus-within:border-white/[0.09]' : 'border-white/15',
          )}
        />
        {!rt.isNotasOwner && (
          <div role="status" aria-live="polite" className="flex items-center gap-1 mt-1">
            <span className="material-symbols-outlined text-[#888] text-[11px]">lock</span>
            <span className="text-[10px] text-[#aaa]">Editando em outro dispositivo</span>
          </div>
        )}

        {/* Thumbnails */}
        {imagens.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-2">
            {imagens.map(img => (
              <div key={img.tempId} className="relative rounded-lg overflow-hidden border border-white/10" style={{ width: 40, height: 40 }}>
                {img.localUrl && <img src={img.localUrl} alt="Imagem da reunião" className="w-full h-full object-cover" />}
                {img.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <span className="material-symbols-outlined text-white text-[10px]">progress_activity</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-t border-white/5 shrink-0">
        <button onClick={paused ? resumeMeeting : pauseMeeting}
          className="h-7 px-3 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] cursor-pointer border transition-colors"
          style={paused
            ? { color: '#090909', background: 'var(--color-hero)', borderColor: 'transparent' }
            : { color: '#666', background: 'transparent', borderColor: 'rgba(255,255,255,0.10)' }}>
          {paused ? '▶ Retomar' : '⏸ Pausa'}
        </button>
        <div className="flex-1" />
        <button onClick={() => setShowEncerrarDlg(true)}
          className="h-7 px-3 rounded-full text-[10px] font-semibold uppercase text-white border-none cursor-pointer"
          style={{ background: 'var(--color-vermelho)' }}>
          Encerrar
        </button>
      </div>
    </div>
    </>
  )
}
